"use client"

import type React from "react"
import { useState, useRef } from "react"
import {
  Box,
  Button,
  Container,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  // Import Close icon
} from "@mui/material"
import {
  Save,
  Refresh,
  Print,
  Receipt,
  Close,
  AccountBalance,
  TrendingUp,
  People,
  AttachMoney,
  Download,
} from "@mui/icons-material"
import { format } from "date-fns"
import { getPayrollData, savePayrollData } from "../../../api/payroll/payrolls"
import { IonPage } from "@ionic/react"

interface PayrollItem {
  payrollId: string
  employeeId: string
  employeeName: string
  basicSalary: number
  rent: number
  perculiar: number
  salary_scale_id: string
  allowances: Record<string, string | number>
  deductions: Record<string, string | number>
  grossSalary: number
  netPay: number
  period: string
}

const PayrollSystem: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const printRef = useRef<HTMLDivElement>(null)

  const [month, setMonth] = useState<string>(format(new Date(), "MM"))
  const [year, setYear] = useState<string>(format(new Date(), "yyyy"))
  const [payrollData, setPayrollData] = useState<PayrollItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string>("")
  const [success, setSuccess] = useState<string>("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [payslipOpen, setPayslipOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<PayrollItem | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const months = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1).toString().padStart(2, "0"),
    label: format(new Date(2000, i, 1), "MMMM"),
  }))

  const years = Array.from({ length: 10 }, (_, i) => ({
    value: (new Date().getFullYear() - i).toString(),
    label: (new Date().getFullYear() - i).toString(),
  }))

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const generateReport = async () => {
    setLoading(true)
    setError("")
    setSuccess("")

    const data = {
      month: month,
      period: `${month}-${year}`,
      year: year,
      business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
      branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
    }

    try {
      const response = await getPayrollData(data)
      console.log(response)
      setPayrollData(response.data.data)
      setPage(0)
      setSuccess("Payroll report generated successfully!")
    } catch (err) {
      setError("Failed to fetch payroll data. Please try again.")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSavePayroll = async () => {
    setConfirmOpen(false)
    setSaving(true)
    setError("")
    setSuccess("")

    try {
      const payload = {
        period: `${month}-${year}`,
        business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
        branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
        payrolls: payrollData.map((item) => ({
          employeeId: item.employeeId,
          salary_scale: item.salary_scale_id,
        })),
      }

      console.log({ payload })
      const response = await savePayrollData(payload)
      console.log(response)
      setSuccess("Payroll data saved successfully!")
    } catch (err) {
      setError("Failed to save payroll data. Please try again.")
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handlePrintPayslip = (employee: PayrollItem) => {
    setSelectedEmployee(employee)
    setPayslipOpen(true)
  }

  const handlePrintAllPayslips = () => {
    // Create a new window for printing all payslips
    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    const printContent = payrollData
      .map((employee) => generatePayslipHTML(employee))
      .join('<div style="page-break-after: always;"></div>')

    printWindow.document.write(`
      <html>
        <head>
          <title>All Payslips - ${format(new Date(`${year}-${month}-01`), "MMMM yyyy")}</title>
          <style>
            ${getPayslipStyles()}
            @media print {
              .page-break { page-break-after: always; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `)

    printWindow.document.close()
    printWindow.print()
  }

  const generatePayslipHTML = (employee: PayrollItem) => {
    const allowanceKeys = getAllAllowanceKeys()
    const deductionKeys = getAllDeductionKeys()
    const totalDeductions = deductionKeys.reduce((total, key) => total + Number(employee.deductions[key] || 0), 0)

    return `
      <div class="payslip">
        <div class="payslip-header">
          <h2>PAYSLIP</h2>
          <p>Period: ${format(new Date(`${year}-${month}-01`), "MMMM yyyy")}</p>
        </div>
        
        <div class="employee-info">
          <h3>Employee Information</h3>
          <p><strong>Name:</strong> ${employee.employeeName}</p>
          <p><strong>Employee ID:</strong> ${employee.employeeId}</p>
          <p><strong>Pay Period:</strong> ${employee.period}</p>
        </div>

        <div class="salary-breakdown">
          <div class="earnings">
            <h4>Earnings</h4>
            <table>
              <tr><td>Basic Salary</td><td>₦${employee.basicSalary}</td></tr>
              <tr><td>Rent Allowance</td><td>₦${employee.rent}</td></tr>
              <tr><td>Peculiar Allowance</td><td>₦${employee.perculiar}</td></tr>
              ${allowanceKeys
                .map(
                  (key) =>
                    `<tr><td>${key.charAt(0).toUpperCase() + key.slice(1)}</td><td>₦${Number(
                      employee.allowances[key] || 0,
                    )}</td></tr>`,
                )
                .join("")}
              <tr class="total"><td><strong>Gross Salary</strong></td><td><strong>₦${employee.grossSalary}</strong></td></tr>
            </table>
          </div>

          <div class="deductions">
            <h4>Deductions</h4>
            <table>
              ${deductionKeys
                .map(
                  (key) =>
                    `<tr><td>${key.charAt(0).toUpperCase() + key.slice(1)}</td><td>₦${Number(
                      employee.deductions[key] || 0,
                    )}</td></tr>`,
                )
                .join("")}
              <tr class="total"><td><strong>Total Deductions</strong></td><td><strong>₦${totalDeductions}</strong></td></tr>
            </table>
          </div>
        </div>

        <div class="net-pay">
          <h3>Net Pay: ₦${employee.netPay}</h3>
        </div>

        <div class="footer">
          <p>This is a computer-generated payslip and does not require a signature.</p>
          <p>Generated on: ${format(new Date(), "dd/MM/yyyy HH:mm")}</p>
        </div>
      </div>
    `
  }

  const getPayslipStyles = () => `
    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
    .payslip { max-width: 800px; margin: 0 auto 40px; border: 2px solid #333; padding: 20px; }
    .payslip-header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
    .payslip-header h2 { margin: 0; font-size: 24px; }
    .employee-info { margin-bottom: 20px; }
    .employee-info h3 { border-bottom: 1px solid #ccc; padding-bottom: 5px; }
    .salary-breakdown { display: flex; gap: 20px; margin-bottom: 20px; }
    .earnings, .deductions { flex: 1; }
    .earnings h4, .deductions h4 { border-bottom: 1px solid #ccc; padding-bottom: 5px; }
    table { width: 100%; border-collapse: collapse; }
    td { padding: 5px; border-bottom: 1px solid #eee; }
    .total td { border-top: 2px solid #333; font-weight: bold; }
    .net-pay { text-align: center; background: #f0f0f0; padding: 15px; border: 2px solid #333; }
    .net-pay h3 { margin: 0; font-size: 20px; }
    .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
  `

  const printSinglePayslip = () => {
    if (!selectedEmployee) return

    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    printWindow.document.write(`
      <html>
        <head>
          <title>Payslip - ${selectedEmployee.employeeName}</title>
          <style>${getPayslipStyles()}</style>
        </head>
        <body>
          ${generatePayslipHTML(selectedEmployee)}
        </body>
      </html>
    `)

    printWindow.document.close()
    printWindow.print()
    setPayslipOpen(false)
  }

  // Get all unique allowance and deduction keys from the data
  const getAllAllowanceKeys = (): string[] => {
    const keys = new Set<string>()
    payrollData.forEach((item) => {
      Object.keys(item.allowances).forEach((key) => keys.add(key))
    })
    return Array.from(keys)
  }

  const getAllDeductionKeys = (): string[] => {
    const keys = new Set<string>()
    payrollData.forEach((item) => {
      Object.keys(item.deductions).forEach((key) => keys.add(key))
    })
    return Array.from(keys)
  }

  // Calculate totals for each column
  const calculateColumnTotals = () => {
    const allowanceKeys = getAllAllowanceKeys()
    const deductionKeys = getAllDeductionKeys()

    const totals = {
      basicSalary: 0,
      rent: 0,
      perculiar: 0,
      allowances: {} as Record<string, number>,
      grossSalary: 0,
      deductions: {} as Record<string, number>,
      totalDeductions: 0,
      netPay: 0,
    }

    allowanceKeys.forEach((key) => {
      totals.allowances[key] = 0
    })

    deductionKeys.forEach((key) => {
      totals.deductions[key] = 0
    })

    payrollData.forEach((item) => {
      totals.basicSalary += item.basicSalary
      totals.rent += item.rent
      totals.perculiar += item.perculiar
      totals.grossSalary += item.grossSalary
      totals.netPay += item.netPay

      let employeeTotalDeductions = 0

      allowanceKeys.forEach((key) => {
        totals.allowances[key] += Number(item.allowances[key] || 0)
      })

      deductionKeys.forEach((key) => {
        const deductionValue = Number(item.deductions[key] || 0)
        totals.deductions[key] += deductionValue
        employeeTotalDeductions += deductionValue
      })

      totals.totalDeductions += employeeTotalDeductions
    })

    return totals
  }

  const columnTotals = calculateColumnTotals()
  const allowanceKeys = getAllAllowanceKeys()
  const deductionKeys = getAllDeductionKeys()

  return (
    <IonPage className="pushDown">


    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: "primary.main" }}>
        <Toolbar>
          <Avatar sx={{ mr: 2, bgcolor: "primary.dark" }}>
            <Receipt />
          </Avatar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Payroll Management System
          </Typography>
          {payrollData.length > 0 && (
            <Button color="inherit" startIcon={<Print />} onClick={handlePrintAllPayslips} sx={{ ml: 2 }}>
              Print All Payslips
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Stats Cards */}
        {payrollData.length > 0 && (
          <Fade in timeout={300}>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                        <People />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                          {payrollData.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Employees
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
                        <TrendingUp />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
                          ₦{columnTotals.grossSalary}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Gross
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: "error.main", mr: 2 }}>
                        <AccountBalance />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: "error.main" }}>
                          ₦{columnTotals.totalDeductions}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Deductions
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: "info.main", mr: 2 }}>
                        <AttachMoney />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: "info.main" }}>
                          ₦{columnTotals.netPay}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Net Pay
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Fade>
        )}

        {/* Controls */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
          <CardHeader
            title="Generate Payroll Report"
            subheader="Select month and year to generate payroll data"
            sx={{ bgcolor: "grey.50" }}
          />
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="month-select-label">Month</InputLabel>
                  <Select
                    labelId="month-select-label"
                    value={month}
                    label="Month"
                    onChange={(e) => setMonth(e.target.value as string)}
                  >
                    {months.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={year}
                    label="Year"
                    onChange={(e) => setYear(e.target.value as string)}
                  >
                    {years.map((y) => (
                      <MenuItem key={y.value} value={y.value}>
                        {y.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  onClick={generateReport}
                  disabled={loading}
                  fullWidth
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
                >
                  Generate Report
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => setConfirmOpen(true)}
                  disabled={loading || payrollData.length === 0 || saving}
                  fullWidth
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                >
                  Save Payroll
                </Button>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {success}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Payroll Data Table */}
        {payrollData.length > 0 && (
          <Slide direction="up" in timeout={500}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6">
                      Payroll Report for {format(new Date(`${year}-${month}-01`), "MMMM yyyy")}
                    </Typography>
                    <Chip label={`${payrollData.length} employees`} color="primary" />
                  </Box>
                }
                action={
                  <Button variant="outlined" startIcon={<Download />} onClick={handlePrintAllPayslips}>
                    Export All
                  </Button>
                }
                sx={{ bgcolor: "grey.50" }}
              />

              <CardContent sx={{ p: 0 }}>
                <TableContainer sx={{ maxHeight: "calc(100vh - 400px)", overflow: "auto" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "primary.main", color: "white" }}>
                          Employee
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "primary.main", color: "white" }}>
                          Basic Salary
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "primary.main", color: "white" }}>
                          Rent
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "primary.main", color: "white" }}>
                          Peculiar
                        </TableCell>
                        {allowanceKeys.map((key) => (
                          <TableCell
                            key={`allowance-${key}`}
                            align="right"
                            sx={{ fontWeight: "bold", bgcolor: "success.main", color: "white" }}
                          >
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "info.main", color: "white" }}>
                          Gross Salary
                        </TableCell>
                        {deductionKeys.map((key) => (
                          <TableCell
                            key={`deduction-${key}`}
                            align="right"
                            sx={{ fontWeight: "bold", bgcolor: "error.main", color: "white" }}
                          >
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "error.main", color: "white" }}>
                          Total Deductions
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: "warning.main", color: "white" }}>
                          Net Pay
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: "bold", bgcolor: "grey.800", color: "white" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payrollData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, i) => {
                        const rowTotalDeductions = deductionKeys.reduce((total, key) => {
                          return total + Number(row.deductions[key] || 0)
                        }, 0)

                        return (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{row.employeeName}</TableCell>
                            <TableCell align="right">₦{row.basicSalary}</TableCell>
                            <TableCell align="right">₦{row.rent}</TableCell>
                            <TableCell align="right">₦{row.perculiar}</TableCell>
                            {allowanceKeys.map((key) => (
                              <TableCell key={`${row.payrollId}-allowance-${key}`} align="right">
                                ₦{Number(row.allowances[key] || 0)}
                              </TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 600, color: "info.main" }}>
                              ₦{row.grossSalary}
                            </TableCell>
                            {deductionKeys.map((key) => (
                              <TableCell key={`${row.payrollId}-deduction-${key}`} align="right">
                                ₦{Number(row.deductions[key] || 0)}
                              </TableCell>
                            ))}
                            <TableCell align="right" sx={{ color: "error.main" }}>
                              ₦{rowTotalDeductions}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: "success.main" }}>
                              ₦{row.netPay}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Print Payslip">
                                <IconButton onClick={() => handlePrintPayslip(row)} color="primary" size="small">
                                  <Print />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )
                      })}

                      {/* Totals row */}
                      <TableRow
                        sx={{
                          "& td": {
                            fontWeight: "bold",
                            backgroundColor: "action.hover",
                            borderTop: "2px solid",
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <TableCell>TOTAL</TableCell>
                        <TableCell align="right">₦{columnTotals.basicSalary}</TableCell>
                        <TableCell align="right">₦{columnTotals.rent}</TableCell>
                        <TableCell align="right">₦{columnTotals.perculiar}</TableCell>
                        {allowanceKeys.map((key) => (
                          <TableCell key={`total-allowance-${key}`} align="right">
                            ₦{columnTotals.allowances[key]}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ color: "info.main" }}>
                          ₦{columnTotals.grossSalary}
                        </TableCell>
                        {deductionKeys.map((key) => (
                          <TableCell key={`total-deduction-${key}`} align="right">
                            ₦{columnTotals.deductions[key]}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ color: "error.main" }}>
                          ₦{columnTotals.totalDeductions}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "success.main" }}>
                          ₦{columnTotals.netPay}
                        </TableCell>
                        <TableCell align="center">-</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={payrollData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: 1, borderColor: "divider" }}
                />
              </CardContent>
            </Card>
          </Slide>
        )}
      </Container>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "success.main" }}>
              <Save />
            </Avatar>
            Confirm Save Payroll
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to save the payroll data for{" "}
            <strong>{format(new Date(`${year}-${month}-01`), "MMMM yyyy")}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action will finalize the payroll for {payrollData.length} employees and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePayroll} color="success" variant="contained" startIcon={<Save />}>
            Confirm Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payslip Preview Dialog */}
      <Dialog open={payslipOpen} onClose={() => setPayslipOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Payslip Preview</Typography>
            <IconButton onClick={() => setPayslipOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEmployee && (
            <Box ref={printRef} sx={{ p: 2 }}>
              <Paper sx={{ p: 3, border: "2px solid", borderColor: "primary.main" }}>
                {/* Payslip Header */}
                <Box sx={{ textAlign: "center", mb: 3, pb: 2, borderBottom: "2px solid", borderColor: "primary.main" }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                    PAYSLIP
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Period: {format(new Date(`${year}-${month}-01`), "MMMM yyyy")}
                  </Typography>
                </Box>

                {/* Employee Info */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "primary.main", borderBottom: 1, borderColor: "divider", pb: 1 }}
                  >
                    Employee Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Name:</strong> {selectedEmployee.employeeName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Employee ID:</strong> {selectedEmployee.employeeId}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Pay Period:</strong> {selectedEmployee.period}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Salary Breakdown */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Earnings */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: "success.main", borderBottom: 1, borderColor: "divider", pb: 1 }}
                    >
                      Earnings
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Basic Salary</TableCell>
                            <TableCell align="right">₦{selectedEmployee.basicSalary}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Rent Allowance</TableCell>
                            <TableCell align="right">₦{selectedEmployee.rent}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Peculiar Allowance</TableCell>
                            <TableCell align="right">₦{selectedEmployee.perculiar}</TableCell>
                          </TableRow>
                          {allowanceKeys.map((key) => (
                            <TableRow key={key}>
                              <TableCell>{key.charAt(0).toUpperCase() + key.slice(1)}</TableCell>
                              <TableCell align="right">
                                ₦{Number(selectedEmployee.allowances[key] || 0)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow
                            sx={{ "& td": { borderTop: "2px solid", borderColor: "success.main", fontWeight: "bold" } }}
                          >
                            <TableCell>Gross Salary</TableCell>
                            <TableCell align="right">₦{selectedEmployee.grossSalary}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  {/* Deductions */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: "error.main", borderBottom: 1, borderColor: "divider", pb: 1 }}
                    >
                      Deductions
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          {deductionKeys.map((key) => (
                            <TableRow key={key}>
                              <TableCell>{key.charAt(0).toUpperCase() + key.slice(1)}</TableCell>
                              <TableCell align="right">
                                ₦{Number(selectedEmployee.deductions[key] || 0)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow
                            sx={{ "& td": { borderTop: "2px solid", borderColor: "error.main", fontWeight: "bold" } }}
                          >
                            <TableCell>Total Deductions</TableCell>
                            <TableCell align="right">
                              ₦
                              {deductionKeys
                                .reduce((total, key) => total + Number(selectedEmployee.deductions[key] || 0), 0)
                                }
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>

                {/* Net Pay */}
                <Box sx={{ textAlign: "center", p: 3, bgcolor: "primary.light", borderRadius: 2, mb: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                    Net Pay: ₦{selectedEmployee.netPay}
                  </Typography>
                </Box>

                {/* Footer */}
                <Box sx={{ textAlign: "center", color: "text.secondary", fontSize: "0.875rem" }}>
                  <Typography variant="body2">
                    This is a computer-generated payslip and does not require a signature.
                  </Typography>
                  <Typography variant="body2">Generated on: {format(new Date(), "dd/MM/yyyy HH:mm")}</Typography>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayslipOpen(false)}>Close</Button>
          <Button onClick={printSinglePayslip} variant="contained" startIcon={<Print />}>
            Print Payslip
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
        </IonPage>
  )
}

export default PayrollSystem
